/**
 * Floor-plan storage aligned with POS client:
 * - Primary: cloud-pos `table_visualization` (GET current + PATCH)
 * - business.metadata.dineInFloorPlanSettings → feature toggles
 * - Migration read fallback: legacy business.metadata.dineInFloorPlan
 *   until first Save creates visualization
 */

import {
  clampTableSize,
  createDefaultSectionLayout,
  createEmptyFloorPlan,
  normalizeOutline,
} from './lib/defaults';
import {
  getCurrentTableVisualization,
  upsertTableVisualization,
} from './tableVisualizationApi';

export {
  createDefaultSectionLayout,
  createEmptyFloorPlan,
  createDefaultTablePlacement,
  resolvePresetForCapacity,
  suggestTableSizeForHall,
  clampTableSize,
} from './lib/defaults';

export const DEFAULT_TABLE_COLOR = '#F8FAFC';
export const DEFAULT_FLOOR_PLAN_SETTINGS = {
  enabled: true,
  arrangeEnabled: true,
  layoutEditorEnabled: false,
};

const isRecord = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseNumber = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const parseShape = (value) => {
  switch (value) {
    case 'square':
    case 'round':
    case 'rectangle':
    case 'booth':
    case 'bar':
      return value;
    default:
      return 'square';
  }
};

const parseBackground = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return 'light';
  }
  switch (value) {
    case 'wood':
    case 'tile':
    case 'concrete':
    case 'dark':
    case 'light':
      return value;
    default:
      break;
  }
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    return `#${trimmed
      .slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('')
      .toUpperCase()}`;
  }
  return 'light';
};

const parseColor = (value) => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const hex = trimmed
      .slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('');
    return `#${hex.toUpperCase()}`;
  }
  return undefined;
};

const OBJECT_KINDS = [
  'door',
  'kitchen',
  'bar',
  'pos',
  'restroom',
  'wall',
  'plant',
  'stairs',
];

const parseObject = (raw, fallbackId) => {
  if (!isRecord(raw)) {
    return null;
  }
  if (typeof raw.kind !== 'string' || !OBJECT_KINDS.includes(raw.kind)) {
    return null;
  }
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : fallbackId,
    kind: raw.kind,
    label: typeof raw.label === 'string' ? raw.label : raw.kind,
    x: parseNumber(raw.x, 40),
    y: parseNumber(raw.y, 40),
    width: parseNumber(raw.width, 80),
    height: parseNumber(raw.height, 60),
    rotation: parseNumber(raw.rotation, 0),
    zIndex: parseNumber(raw.zIndex, 1),
  };
};

const parsePlacement = (raw) => {
  if (!isRecord(raw)) {
    return null;
  }
  const color = parseColor(raw.color);
  // The floor plan document is the single source of truth for the builder, so
  // table identity (name/displayName/capacity) is embedded in the placement and
  // saved with the one table-visualization PATCH — no /cloud-pos/table needed.
  const name = typeof raw.name === 'string' && raw.name ? raw.name : undefined;
  const displayName =
    typeof raw.displayName === 'string' && raw.displayName
      ? raw.displayName
      : undefined;
  const capacity =
    typeof raw.capacity === 'number' && Number.isFinite(raw.capacity)
      ? raw.capacity
      : undefined;
  return {
    x: parseNumber(raw.x, 0),
    y: parseNumber(raw.y, 0),
    width: clampTableSize(parseNumber(raw.width, 100)),
    height: clampTableSize(parseNumber(raw.height, 100)),
    rotation: parseNumber(raw.rotation, 0),
    shape: parseShape(raw.shape),
    zIndex: parseNumber(raw.zIndex, 1),
    ...(color ? { color } : {}),
    ...(name ? { name } : {}),
    ...(displayName ? { displayName } : {}),
    ...(capacity !== undefined ? { capacity } : {}),
  };
};

const parseOutline = (raw) => {
  const outlineRaw = Array.isArray(raw) ? raw : [];
  return normalizeOutline(
    outlineRaw
      .map((point) => {
        if (!isRecord(point)) {
          return null;
        }
        return {
          x: parseNumber(point.x, 0),
          y: parseNumber(point.y, 0),
        };
      })
      .filter(Boolean)
  );
};

const parseObjectsMap = (objectsRaw) => {
  const source = isRecord(objectsRaw) ? objectsRaw : {};
  const objects = {};
  Object.entries(source).forEach(([objectId, objectRaw]) => {
    const object = parseObject(objectRaw, objectId);
    if (object) {
      objects[object.id] = object;
    }
  });
  return objects;
};

export const parseSectionFloorPlanMeta = (raw) => {
  if (!isRecord(raw)) {
    return null;
  }
  const defaults = createDefaultSectionLayout();
  const styleRaw = isRecord(raw.style) ? raw.style : {};
  const canvasRaw = isRecord(raw.canvas) ? raw.canvas : {};
  return {
    style: {
      background: parseBackground(styleRaw.background),
      showGrid:
        typeof styleRaw.showGrid === 'boolean'
          ? styleRaw.showGrid
          : defaults.style.showGrid,
      gridSize: parseNumber(styleRaw.gridSize, defaults.style.gridSize),
    },
    canvas: {
      width: parseNumber(canvasRaw.width, defaults.canvas.width),
      height: parseNumber(canvasRaw.height, defaults.canvas.height),
    },
    outline: parseOutline(raw.outline),
    objects: parseObjectsMap(raw.objects),
  };
};

export const parseTableFloorPlanMeta = (raw) => parsePlacement(raw);

const parseSectionLayout = (raw) => {
  const defaults = createDefaultSectionLayout();
  if (!isRecord(raw)) {
    return defaults;
  }
  const chrome = parseSectionFloorPlanMeta(raw) || {
    style: defaults.style,
    canvas: defaults.canvas,
    outline: defaults.outline,
    objects: {},
  };
  const tablesRaw = isRecord(raw.tables) ? raw.tables : {};
  const tables = {};
  Object.entries(tablesRaw).forEach(([uuid, placementRaw]) => {
    const placement = parsePlacement(placementRaw);
    if (placement) {
      tables[uuid] = placement;
    }
  });
  const name = typeof raw.name === 'string' && raw.name ? raw.name : undefined;
  const displayName =
    typeof raw.displayName === 'string' && raw.displayName
      ? raw.displayName
      : undefined;
  return {
    ...chrome,
    ...(name ? { name } : {}),
    ...(displayName ? { displayName } : {}),
    tables,
  };
};

export const parseFloorPlan = (raw) => {
  if (!isRecord(raw) || !isRecord(raw.sections)) {
    return createEmptyFloorPlan();
  }
  const version =
    typeof raw.version === 'number' && Number.isFinite(raw.version)
      ? raw.version
      : 1;
  const sections = {};
  Object.entries(raw.sections).forEach(([sectionUuid, sectionRaw]) => {
    sections[sectionUuid] = parseSectionLayout(sectionRaw);
  });
  return { version, sections };
};

/** Parse a `table_visualization` API document into DineInFloorPlan. */
export const readFloorPlanFromVisualization = (doc) => {
  if (!isRecord(doc)) {
    return createEmptyFloorPlan();
  }
  const sectionsRaw =
    typeof doc.sections === 'string'
      ? (() => {
          try {
            return JSON.parse(doc.sections);
          } catch {
            return null;
          }
        })()
      : doc.sections;
  return parseFloorPlan({
    version: doc.version,
    sections: sectionsRaw,
  });
};

/**
 * Prefer visualization doc; else legacy business.metadata.dineInFloorPlan.
 * Catalog create/update/delete is handled separately on Save
 * (see syncFloorPlanCatalog.js).
 */
export const resolveFloorPlan = ({ visualizationDoc, legacyBusiness }) => {
  if (visualizationDoc) {
    return deepCloneFloorPlan(readFloorPlanFromVisualization(visualizationDoc));
  }
  const legacy = readFloorPlanFromBusiness(legacyBusiness);
  if (Object.keys(legacy.sections || {}).length) {
    return deepCloneFloorPlan(legacy);
  }
  return createEmptyFloorPlan();
};

export const readDetailsMetadata = (details) => {
  if (!isRecord(details)) {
    return {};
  }
  return isRecord(details.metadata) ? { ...details.metadata } : {};
};

/** Keep floor-plan metadata when renaming / updating catalog rows. */
export const withPreservedDetailsMetadata = (updatedDetails, previousDetails) => {
  const previousMetadata = readDetailsMetadata(previousDetails);
  if (!Object.keys(previousMetadata).length) {
    return updatedDetails;
  }
  const updatedMetadata = isRecord(updatedDetails?.metadata)
    ? updatedDetails.metadata
    : {};
  return {
    ...updatedDetails,
    metadata: {
      ...previousMetadata,
      ...updatedMetadata,
    },
  };
};

const readBusinessMetadata = (business) => {
  if (!isRecord(business)) {
    return {};
  }
  const rootMeta = isRecord(business.metadata) ? business.metadata : {};
  const detailsMeta = isRecord(business.businessDetails?.metadata)
    ? business.businessDetails.metadata
    : {};
  return { ...rootMeta, ...detailsMeta };
};

/** Legacy monolith layout on business metadata. */
export const readFloorPlanFromBusiness = (business) => {
  const meta = readBusinessMetadata(business);
  return parseFloorPlan(meta.dineInFloorPlan);
};

/**
 * Assemble in-memory plan from section/table metadata.
 * Falls back to legacy business.dineInFloorPlan when an entity has no floorPlan yet.
 */
export const assembleFloorPlanFromCatalog = (
  sections,
  tables,
  legacyBusiness
) => {
  const legacy = readFloorPlanFromBusiness(legacyBusiness);
  const plan = createEmptyFloorPlan();

  (sections || []).forEach((section) => {
    const details = isRecord(section?.sectionDetails)
      ? section.sectionDetails
      : null;
    const sectionUuid =
      details && typeof details.uuid === 'string' ? details.uuid : null;
    if (!sectionUuid) {
      return;
    }
    const sectionLabel =
      (typeof details.displayName === 'string' && details.displayName) ||
      (typeof details.name === 'string' && details.name) ||
      undefined;
    const metadata = readDetailsMetadata(details);
    const fromEntity = parseSectionFloorPlanMeta(metadata.floorPlan);
    const fromLegacy = legacy.sections[sectionUuid];

    if (fromEntity) {
      plan.sections[sectionUuid] = {
        ...(sectionLabel
          ? { name: sectionLabel, displayName: sectionLabel }
          : {}),
        style: fromEntity.style,
        canvas: fromEntity.canvas,
        outline: fromEntity.outline,
        objects: fromEntity.objects,
        tables: {},
      };
      return;
    }

    if (fromLegacy) {
      plan.sections[sectionUuid] = {
        ...(sectionLabel
          ? { name: sectionLabel, displayName: sectionLabel }
          : {}),
        style: fromLegacy.style,
        canvas: fromLegacy.canvas,
        outline: fromLegacy.outline,
        objects: { ...(fromLegacy.objects || {}) },
        tables: {},
      };
    }
  });

  (tables || []).forEach((table) => {
    const details = isRecord(table?.tableDetails) ? table.tableDetails : null;
    const tableUuid =
      details && typeof details.uuid === 'string' ? details.uuid : null;
    const sectionUuid =
      details && typeof details.sectionUuid === 'string'
        ? details.sectionUuid
        : null;
    if (!tableUuid || !sectionUuid) {
      return;
    }

    const metadata = readDetailsMetadata(details);
    const fromEntity = parseTableFloorPlanMeta(metadata.floorPlan);
    const fromLegacy = legacy.sections[sectionUuid]?.tables[tableUuid] || null;
    const basePlacement = fromEntity || fromLegacy;
    if (!basePlacement) {
      return;
    }
    // Embed identity so migrated tables keep their name/seats in the doc model.
    const tableLabel =
      (typeof details.displayName === 'string' && details.displayName) ||
      (typeof details.name === 'string' && details.name) ||
      undefined;
    const capacity =
      typeof details.capacity === 'number' &&
      Number.isFinite(details.capacity)
        ? details.capacity
        : undefined;
    const placement = {
      ...basePlacement,
      ...(tableLabel
        ? { name: tableLabel, displayName: tableLabel }
        : {}),
      ...(capacity !== undefined ? { capacity } : {}),
    };

    if (!plan.sections[sectionUuid]) {
      const legacySection = legacy.sections[sectionUuid];
      if (legacySection) {
        plan.sections[sectionUuid] = {
          style: legacySection.style,
          canvas: legacySection.canvas,
          outline: legacySection.outline,
          objects: { ...(legacySection.objects || {}) },
          tables: {},
        };
      } else {
        plan.sections[sectionUuid] = createDefaultSectionLayout();
      }
    }

    plan.sections[sectionUuid].tables[tableUuid] = placement;
  });

  return plan;
};

const asBool = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '1' || normalized === 'true') {
      return true;
    }
    if (normalized === '0' || normalized === 'false') {
      return false;
    }
  }
  return fallback;
};

const readDineInOptions = (business) => {
  const settings = isRecord(business?.settings) ? business.settings : {};
  const settingsMeta = isRecord(settings.metadata) ? settings.metadata : {};
  const fromSettings = isRecord(settingsMeta.dineInOptions)
    ? settingsMeta.dineInOptions
    : {};
  const meta = readBusinessMetadata(business);
  const metaSettings = isRecord(meta.settings) ? meta.settings : {};
  const fromMeta = isRecord(metaSettings.dineInOptions)
    ? metaSettings.dineInOptions
    : {};

  return {
    layoutEditorEnabled: asBool(
      fromSettings.layoutEditorEnabled ?? fromMeta.layoutEditorEnabled,
      DEFAULT_FLOOR_PLAN_SETTINGS.layoutEditorEnabled
    ),
  };
};

export const isLayoutEditorEnabled = (business) =>
  readDineInOptions(business).layoutEditorEnabled;

export const readFloorPlanSettingsFromBusiness = (business) => {
  if (!isRecord(business)) {
    return { ...DEFAULT_FLOOR_PLAN_SETTINGS };
  }
  const meta = readBusinessMetadata(business);
  const raw = isRecord(meta.dineInFloorPlanSettings)
    ? meta.dineInFloorPlanSettings
    : {};
  return {
    enabled: typeof raw.enabled === 'boolean' ? raw.enabled : true,
    arrangeEnabled:
      typeof raw.arrangeEnabled === 'boolean' ? raw.arrangeEnabled : true,
    ...readDineInOptions(business),
  };
};

export const ensureSectionLayout = (plan, sectionUuid) => {
  if (plan.sections[sectionUuid]) {
    return plan;
  }
  return {
    ...plan,
    sections: {
      ...plan.sections,
      [sectionUuid]: createDefaultSectionLayout(),
    },
  };
};

const mergeMetadataKeys = (business, patch) => {
  const nextMetadata = {
    ...(isRecord(business.metadata) ? business.metadata : {}),
    ...patch,
  };
  const next = {
    ...business,
    metadata: nextMetadata,
  };
  if (isRecord(business.businessDetails)) {
    next.businessDetails = {
      ...business.businessDetails,
      metadata: {
        ...(isRecord(business.businessDetails.metadata)
          ? business.businessDetails.metadata
          : {}),
        ...patch,
      },
    };
  }
  return next;
};

/** Remove a metadata key from both root and businessDetails.metadata. */
const omitMetadataKey = (business, key) => {
  const strip = (meta) => {
    if (!isRecord(meta) || !(key in meta)) {
      return meta;
    }
    const next = { ...meta };
    delete next[key];
    return next;
  };
  const next = {
    ...business,
    metadata: strip(isRecord(business.metadata) ? business.metadata : {}),
  };
  if (isRecord(business.businessDetails)) {
    next.businessDetails = {
      ...business.businessDetails,
      metadata: strip(
        isRecord(business.businessDetails.metadata)
          ? business.businessDetails.metadata
          : {}
      ),
    };
  }
  return next;
};

/** Clear legacy business layout blob after catalog migration. */
export const clearLegacyFloorPlanFromBusiness = (business) =>
  omitMetadataKey(business, 'dineInFloorPlan');

export const mergeFloorPlanSettingsIntoBusiness = (business, settings) => {
  const currentMeta = readBusinessMetadata(business);
  const currentMetaSettings = isRecord(currentMeta.settings)
    ? currentMeta.settings
    : {};
  const currentMetaOptions = isRecord(currentMetaSettings.dineInOptions)
    ? currentMetaSettings.dineInOptions
    : {};
  const withMetadata = mergeMetadataKeys(business, {
    dineInFloorPlanSettings: {
      enabled: settings.enabled !== false,
      arrangeEnabled: settings.arrangeEnabled !== false,
    },
    settings: {
      ...currentMetaSettings,
      dineInOptions: {
        ...currentMetaOptions,
        layoutEditorEnabled: settings.layoutEditorEnabled ? 1 : 0,
      },
    },
  });

  const currentSettings = isRecord(withMetadata.settings)
    ? withMetadata.settings
    : {};
  const currentSettingsMeta = isRecord(currentSettings.metadata)
    ? currentSettings.metadata
    : {};
  const currentSettingsOptions = isRecord(currentSettingsMeta.dineInOptions)
    ? currentSettingsMeta.dineInOptions
    : {};

  return {
    ...withMetadata,
    settings: {
      ...currentSettings,
      metadata: {
        ...currentSettingsMeta,
        dineInOptions: {
          ...currentSettingsOptions,
          layoutEditorEnabled: settings.layoutEditorEnabled ? 1 : 0,
        },
      },
    },
  };
};

/**
 * @deprecated Prefer saveFloorPlanToCatalog. Kept only for accidental imports.
 */
export const mergeFloorPlanIntoBusiness = (business, plan) =>
  mergeMetadataKeys(business, { dineInFloorPlan: plan });

export const deepCloneFloorPlan = (plan) => JSON.parse(JSON.stringify(plan));

const readEntityDetails = (entity, detailsKey) =>
  entity?.[detailsKey] || entity || null;

/**
 * `/cloud-pos/section` and `/cloud-pos/table` are the source of truth for which
 * sections/tables exist and for their name and seat count. The visualization
 * document only owns layout, so a freshly loaded plan is reconciled against the
 * catalog: rows deleted (or moved) elsewhere drop off the floor, and embedded
 * identity is refreshed from the catalog.
 *
 * Pruning is skipped for an empty catalog list so a failed/empty fetch can never
 * wipe a saved floor plan.
 */
export const applyCatalogTruthToPlan = (
  plan,
  catalogSections = [],
  catalogTables = []
) => {
  const sectionByUuid = new Map();
  catalogSections.forEach((section) => {
    const details = readEntityDetails(section, 'sectionDetails');
    if (details?.uuid) {
      sectionByUuid.set(details.uuid, details);
    }
  });
  const tableByUuid = new Map();
  catalogTables.forEach((table) => {
    const details = readEntityDetails(table, 'tableDetails');
    if (details?.uuid) {
      tableByUuid.set(details.uuid, details);
    }
  });

  const pruneSections = sectionByUuid.size > 0;
  const pruneTables = tableByUuid.size > 0;

  const next = { version: plan?.version ?? 1, sections: {} };
  Object.entries(plan?.sections || {}).forEach(([sectionUuid, layout]) => {
    const sectionDetails = sectionByUuid.get(sectionUuid);
    if (!sectionDetails && pruneSections) {
      return;
    }
    const sectionLabel =
      sectionDetails?.displayName ||
      sectionDetails?.name ||
      layout?.displayName ||
      layout?.name ||
      'Section';

    const tables = {};
    Object.entries(layout?.tables || {}).forEach(([tableUuid, placement]) => {
      const details = tableByUuid.get(tableUuid);
      if (!details) {
        if (!pruneTables) {
          tables[tableUuid] = placement;
        }
        return;
      }
      if (details.sectionUuid && details.sectionUuid !== sectionUuid) {
        // Moved to another section in the catalog — no longer on this floor.
        return;
      }
      const name = details.name || details.displayName || placement?.name;
      const displayName =
        details.displayName || details.name || placement?.displayName;
      const capacity = Number(details.capacity);
      tables[tableUuid] = {
        ...placement,
        ...(name ? { name } : {}),
        ...(displayName ? { displayName } : {}),
        ...(Number.isFinite(capacity) ? { capacity } : {}),
      };
    });

    next.sections[sectionUuid] = {
      ...layout,
      name: sectionLabel,
      displayName: sectionLabel,
      tables,
    };
  });
  return next;
};

/**
 * Load current floor plan from cloud-pos table-visualization.
 * Returns `{ doc, plan }` — doc null when none exists (404).
 */
export const fetchCurrentFloorPlan = async (businessUuid) => {
  if (!businessUuid) {
    return { doc: null, plan: createEmptyFloorPlan() };
  }
  try {
    const doc = await getCurrentTableVisualization();
    if (!doc) {
      return { doc: null, plan: createEmptyFloorPlan() };
    }
    return { doc, plan: readFloorPlanFromVisualization(doc) };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) {
      return { doc: null, plan: createEmptyFloorPlan() };
    }
    throw err;
  }
};

/**
 * Persist full floor plan via cloud-pos table-visualization (POST or PATCH).
 * Returns `{ snapshot, doc, created }`.
 */
export const saveFloorPlanToCatalog = async ({ businessUuid, plan }) => {
  if (!businessUuid) {
    throw new Error('businessUuid is required to save table-visualization');
  }
  const snapshot = deepCloneFloorPlan(plan);
  const { doc, created } = await upsertTableVisualization({
    version: snapshot.version,
    sections: snapshot.sections || {},
  });
  return { snapshot, doc, created, wroteEntityMetadata: false };
};
